import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { getErrorMessage } from '../lib/errors'
import { CardNB } from './ui-nb'

interface NotificationLogEntry {
  id: string
  order_id: string
  template_name: string | null
  channel: string
  recipient_user_id: string | null
  to_phone: string | null
  to_email: string | null
  title: string | null
  body: string | null
  status: 'pending' | 'sent' | 'failed' | 'skipped' | 'subscribed'
  skipped_reason: string | null
  created_at: string
}

interface NotificationHistoryCardProps {
  orderId: string
}

// Channel icons as emoji (matching the UI-NB pattern)
const channelIcons: Record<string, string> = {
  sms: '💬',
  email: '📧',
  push: '🔔',
  whatsapp: '📱',
}

// Status badge variants
const statusColors: Record<string, { bg: string; text: string }> = {
  sent: { bg: 'bg-nb-green/10', text: 'text-nb-green' },
  failed: { bg: 'bg-nb-red/10', text: 'text-nb-red' },
  skipped: { bg: 'bg-nb-gray/10', text: 'text-nb-gray' },
  pending: { bg: 'bg-nb-yellow/10', text: 'text-nb-yellow' },
  subscribed: { bg: 'bg-blue-50', text: 'text-blue-600' },
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function NotificationRow({ notification }: { notification: NotificationLogEntry }) {
  const icon = channelIcons[notification.channel] || '📬'
  const statusStyle = statusColors[notification.status] || statusColors.pending

  return (
    <div className="flex gap-3 py-3 border-b border-nb-gray/20 last:border-0">
      {/* Channel icon */}
      <div className="text-xl w-8 text-center">{icon}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-nb-black truncate">
              {notification.title || notification.template_name || notification.channel}
            </p>
            {notification.body && (
              <p className="text-xs text-nb-text-muted mt-0.5 line-clamp-2">
                {notification.body}
              </p>
            )}
            {notification.skipped_reason && (
              <p className="text-xs text-nb-red mt-0.5">
                Reason: {notification.skipped_reason}
              </p>
            )}
          </div>

          {/* Status badge */}
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${statusStyle.bg} ${statusStyle.text}`}
          >
            {notification.status}
          </span>
        </div>

        {/* Timestamp */}
        <p className="text-xs text-nb-text-muted mt-1">
          {formatTimestamp(notification.created_at)}
        </p>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 py-3">
          <div className="w-8 h-8 bg-nb-gray/20 rounded" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-nb-gray/20 rounded w-3/4" />
            <div className="h-3 bg-nb-gray/20 rounded w-1/2" />
            <div className="h-3 bg-nb-gray/20 rounded w-1/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function NotificationHistoryCard({ orderId }: NotificationHistoryCardProps) {
  const [notifications, setNotifications] = useState<NotificationLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchNotifications = async () => {
    try {
      setError(null)
      const { data, error: fetchError } = await supabase.functions.invoke(
        'get-notification-history',
        {
          body: { order_id: orderId },
        }
      )

      if (fetchError) {
        throw new Error(fetchError.message || 'Failed to fetch notifications')
      }

      setNotifications((data as { notifications?: NotificationLogEntry[] })?.notifications || [])
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load notifications')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!orderId) {
      setNotifications([])
      setIsLoading(false)
      return
    }

    fetchNotifications()
  }, [orderId])

  return (
    <CardNB className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase">
          Notifications
        </h2>
        {error && (
          <button
            onClick={fetchNotifications}
            className="text-xs text-nb-black font-bold hover:underline"
          >
            Retry
          </button>
        )}
      </div>

      {isLoading && <LoadingSkeleton />}

      {!isLoading && error && (
        <p className="text-sm text-nb-red text-center py-4">{error}</p>
      )}

      {!isLoading && !error && notifications.length === 0 && (
        <p className="text-sm text-nb-text-muted text-center py-4">
          No notifications sent
        </p>
      )}

      {!isLoading && !error && notifications.length > 0 && (
        <div className="divide-y divide-nb-gray/10">
          {notifications.map((notification) => (
            <NotificationRow key={notification.id} notification={notification} />
          ))}
        </div>
      )}
    </CardNB>
  )
}
